import {api, apiAbs, setSessionLinkIds} from 'portal-core/api';
import _ from 'lodash';
import {pathLastSegment, pathSecondLastSegnment} from '../utils/stringUtils';
import {env} from '../env';
import {traverseObject} from '../utils/objectUtils';
import {dateToString, today} from '../utils/dateUtils';
import {navigateWithData } from '../navigation/navigationHistory';
import insuredNew from '../../../app/config/insured-new.json5';
import {GA_CONFIGURATION, GA_PRODUCT_CODE_KEY,GA_PRODUCT_VERSION_KEY, sendGoogleAnalyticsEvent} from "../utils/googleAnalyticsUtils";
import {setQouteOrAppNumber} from "./index";

export function getProviderCode(carrierGroupId, postalCode, licenseClass, customerRef, effectiveDate) {
  const limitCnt = env.producerCodeLimit ? env.producerCodeLimit : '20';
  const providerLookupToken = "@\$" + env.producerCode;
  // Replace provider lookup+ref by sending producerCode for lookup remotely
  return postApplication(carrierGroupId, postalCode, licenseClass, providerLookupToken, customerRef, effectiveDate);
}

export function postApplication(carrierGroupId, postalCode, licenseClass, providerCd, customerRef, effectiveDate) {

  return api.get(`/products?carrierGroupId=${carrierGroupId}&postalCode=${postalCode}&licenseClass=${licenseClass}`).then(TO_DATA).then(producers => {
    if (Object.entries(producers).length === 0 && producers.constructor === Object) {
      return
    }
    const effectiveDt = effectiveDate ? effectiveDate : dateToString(today());
    const typeOfPolicy = producers.productListItems[0].quickQuoteEnabledInd ? 'QuickQuote' : 'Quote';
    const businessSourceCd = customerRef ? 'ServicePortal::' + customerRef : 'DirectPortal';
    let applicationObj = {
      typeCd: typeOfPolicy,
      basicPolicy: {
        businessSourceCd: businessSourceCd,
        productVersionIdRef: producers.productListItems[0].productVersionId,
        providerRef: providerCd,
        effectiveDt: effectiveDt
      },
    };
    localStorage.setItem(GA_PRODUCT_CODE_KEY,licenseClass);
    localStorage.setItem(GA_PRODUCT_VERSION_KEY,producers.productListItems[0].productVersionId);
    sendGoogleAnalyticsEvent(GA_CONFIGURATION.Quote_Started,true);
    // applicationObj.insured = Object.assign(insuredObj);
    applicationObj.insured = Object.assign(insuredNew);

    return api.post('/applications', applicationObj)
  })
}

export function startApplication({carrierGroupId, postalCode, licenseClass, customerRef, effectiveDate}) {
  return getProviderCode(carrierGroupId, postalCode, licenseClass, customerRef, effectiveDate)
      .then(response => systemIdFromLocation(response.headers.location));
}

function systemIdFromLocation(location) {
  return pathLastSegment(location);
}

function resourceDetailsFromLocation(location) {
  const resource = pathSecondLastSegnment(location);
  const id = pathLastSegment(location);
  return {resource, id};
}

export function getApplication(systemId) {
  return api.get('/applications/' + systemId + '/full').then(TO_DATA);
}

const TO_DATA = r => (r ? r.data : null);


export function getUWData(applicationSystemId) {
  return getApplication(applicationSystemId).then(application => {
    const links = _.keyBy(application._links, 'rel');
    // axios.all([
    return Promise.all([

      apiAbs.get(links.productSetupFull.href).then(TO_DATA),
      apiAbs.get(links.systemCodeRefs.href).then(TO_DATA)

    ]).then(([productSetup, codeRefs]) => {
      const productId = application.basicPolicy.productVersionIdRef;

      return {
        application,
        productInfo: {
          productId,
          productSetup,
          codeRefs,
        }
      }
    });

  });
}

function getFieldFromItemWithType(listOfItems, typeName, typeValue, fieldName) {
  for (const item of listOfItems) {
    if (item.hasOwnProperty(typeName) && item[typeName] === typeValue) {
      return (item.hasOwnProperty(fieldName) ? item[fieldName] : '');
    }
  }
  return (null);
}

function postalCodeArea(postalCode) {
  const parts = postalCode ? postalCode.split('-') : [ '' ];
  return ((parts.length == 2) && (parts[0].length == 5)) ? parts[0] : postalCode;  // strip off US +4 from postal code
}

export const putApplication = application => new Promise((resolve, reject) => {
  const party = application.insured.partyInfo[0];
  let postalCode = getFieldFromItemWithType(party.addresses, 'addrTypeCd', 'InsuredLookupAddr', 'postalCode');
  let emailAddress = party.emailInfo.emailAddr;
  let phoneNumber = getFieldFromItemWithType(party.phoneInfo, 'phoneTypeCd', 'InsuredPhonePrimary', 'phoneNumber');
  let birthDate = (party.personInfo.hasOwnProperty('birthDate') ? party.personInfo.birthDate : '');
  if ( ! postalCode) {
    postalCode = getFieldFromItemWithType(party.addresses, 'addrTypeCd', 'InsuredMailingAddr', 'postalCode');
  }
  setSessionLinkIds(postalCodeArea(postalCode), emailAddress, phoneNumber, birthDate);
  api
    .put('/applications/' + application.systemId + '/full', removeTempFields(application))
    .then(request => resolve(TO_DATA(request)))
    .catch((error) => {
      if (error.response && (error.response.status == 409)) {
        // Error 409 (conflict) after session link update, so retry the PUT request
        retryPut(application, resolve, reject);
      } else {
        reject(error);
      }
    });
});

function retryPut(application, resolve, reject) {
  // copy the revision and updated session link to this application object and reissue
  return getApplication(application.systemId).then(updatedApp => {
    application.updateCount = updatedApp.updateCount;
    application._revision = updatedApp._revision;
    application.basicPolicy.sessionLink = updatedApp.basicPolicy.sessionLink;
    api
        .put('/applications/' + application.systemId + '/full', removeTempFields(application))
        .then(request => resolve(TO_DATA(request)))
        .catch(reject);
  });
}

export function convertToQuote(systemId, typeCd) {
  if (typeCd === "QuickQuote") {
    return api.post(`/applications/${systemId}/convertToQuoteRequest`)
        .then(response => systemIdFromLocation(response.headers.location));
  } else if (typeCd === "Quote") {
    return api.post(`/applications/${systemId}/bindRequest`)
        .then(response => systemIdFromLocation(response.headers.location));
  }
}

export const retrieveQuote = async (request) => {
  // Return the systemId for the quote/application number (eg: QT-0000XXXX)
  const {postalCode, phoneNumber, birthDate, emailAddress, quoteOrAppNumber} = request;
  return await api.put(
      '/applications/updateSessionLinkRequest',
      {postalCode, phoneNumber, birthDate, emailAddress},
      { params: {quoteOrAppNumber} }
  ).then(response => {
    setQouteOrAppNumber(quoteOrAppNumber);
    setSessionLinkIds(postalCode, emailAddress, phoneNumber, birthDate);
    return (systemIdFromLocation(response.headers.location));
  });
}

export function retrieveQuoteFromToken(request) {
  // Return the systemId for the quote/application token (from /clients/XXX/portalLinkRequest)
  const {resourceToken, clientId} = request;
  return (api.get(`/clients/${clientId}/resourceLink?token=${resourceToken}`)
      .then(response => systemIdFromLocation(response.headers.location)));
}

export function retrieveResourceFromToken(request) {
  // Return the systemId for the quote/application token (from /clients/XXX/portalLinkRequest)
  const {resourceToken, clientId} = request;
  return (api.get(`/clients/${clientId}/resourceLink?token=${resourceToken}`)
      .then(response => resourceDetailsFromLocation(response.headers.location)));
}

export function removeTempFields(app) {
  traverseObject(app, bean => {
    if (bean._tempId) {
      delete bean._tempId;
    }
  });
  return app;
}

export const onSubmitAgent = async (resourceId, applicationNumber) => {
  try {
    sendGoogleAnalyticsEvent({...GA_CONFIGURATION.Refer_To_Agent,event_path_from:window.location.pathname});
    const agentResponse = await api.post(
        `/applications/${resourceId}/consumerAgentRequest`
    );
    if (agentResponse.status === 200 || agentResponse.status === 201) {
      navigateWithData('/agent-success', applicationNumber)
    } else if (agentResponse.status === 'error') {
      throw new Error('Agent response returned error');
    }
  } catch (err) {
    navigateWithData('/agent-error', applicationNumber)
  }
}

export function getQuestionsForProduct(systemId, applState) {
  const query = (applState ? ('?applicationState=' + applState) : '');
  return api.get('/applications/' + systemId + '/availableQuestions' + query).then(TO_DATA);
}

export function getQuestionsForProductRisk(systemId, lineCd, riskTypeCd, applState) {
  const query = (applState ? ('?applicationState=' + applState) : '');
  return api.get('/applications/' + systemId + '/lines/' + lineCd + '/availableRiskTypes/' + riskTypeCd + '/availableQuestions' + query).then(TO_DATA);
}

export function getQuestionsForDriver(systemId, driverTypeCd, applState) {
  const query = (applState ? ('?applicationState=' + applState) : '');
  return api.get('/applications/' + systemId + '/availableDriverTypes/' + driverTypeCd + '/availableQuestions' + query).then(TO_DATA);
}

export const getRiskCoverages = async (systemId,lineCd,riskNum,incDeleted=false) => {
  return  api.get(`/applications/${systemId}/lines/${lineCd}/risks/${riskNum}/coverages?includeDeleted=${incDeleted}`).then(TO_DATA);
}

export function getPolicyHashForPortal(policyNumber) {
  const request = { id: policyNumber, container: 'Policy' };
  return api.post('/clients/consumersalesportal/portalLinkRequest', request).then(TO_DATA);
}